import { useState } from 'react';
import type { BrandTag, CreateTagDto, FetchAllBrandTag, UpdateTagDto } from 'types/brandTag';
import useHandleApi from './useHandleApi';

export default function useBrandTagsApi() {
  const [isLoading, setIsLoading] = useState(false);
  const { handleApi, loading: apiLoading } = useHandleApi({
    apiDomain: import.meta.env.VITE_PLATFORM_API_URL
  });

  const loading = isLoading || apiLoading;

  const fetchTags = async (deprecated?: boolean) => {
    setIsLoading(true);

    const endpoint =
      deprecated !== undefined ? `/brand-tags?deprecated=${deprecated}` : '/brand-tags';

    const response = await handleApi({
      endpoint
    });

    if (response.data) {
      const fetchedTags = response.data as FetchAllBrandTag[];

      setIsLoading(false);
      return fetchedTags;
    }

    setIsLoading(false);
    return [];
  };

  const fetchActiveTags = () => fetchTags(false);

  const fetchTagById = async (id: string) => {
    setIsLoading(true);

    const response = await handleApi({
      endpoint: `/brand-tags/${id}`
    });

    setIsLoading(false);
    if (response.data) {
      const tag = response.data as BrandTag;
      tag.createdBy = tag.createdBy.replace('google-apps|', '');
      return tag;
    }
    return null;
  };

  const createTag = async (data: CreateTagDto) => {
    setIsLoading(true);
    try {
      const response = await handleApi({
        endpoint: '/brand-tags',
        method: 'POST',
        data
      });
      return response.data as BrandTag | undefined;
    } finally {
      setIsLoading(false);
    }
  };

  const updateTag = async (id: string, data: UpdateTagDto) => {
    setIsLoading(true);
    try {
      const response = await handleApi({
        endpoint: `/brand-tags/${id}`,
        method: 'PATCH',
        data
      });
      return response.data as BrandTag | undefined;
    } finally {
      setIsLoading(false);
    }
  };

  const getCategories = async (deprecated?: boolean) => {
    const response = await handleApi({
      endpoint: `/brand-tags/categories${deprecated !== undefined ? `?deprecated=${deprecated}` : ''}`
    });

    return (response.data as string[]) || [];
  };

  const getSubCategories = async (category: string, deprecated?: boolean) => {
    const response = await handleApi({
      endpoint: `/brand-tags/subcategories?category=${encodeURIComponent(category)}${deprecated !== undefined ? `&deprecated=${deprecated}` : ''}`
    });

    return (response.data as string[]) || [];
  };

  const getGenders = async (deprecated?: boolean) => {
    const response = await handleApi({
      endpoint: `/brand-tags/genders${deprecated !== undefined ? `?deprecated=${deprecated}` : ''}`
    });

    return (response.data as string[]) || [];
  };

  const deleteTag = async (id: string) => {
    setIsLoading(true);
    try {
      const response = await handleApi({
        endpoint: `/brand-tags/${id}`,
        method: 'DELETE'
      });
      return response.data;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    loading,
    fetchActiveTags,
    fetchTags,
    fetchTagById,
    updateTag,
    createTag,
    deleteTag,
    getCategories,
    getSubCategories,
    getGenders
  };
}
